import { useCssVars as _useCssVars, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-25772f88"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "file-upload-container" }
const _hoisted_2 = {
  key: 0,
  class: "file-list"
}
const _hoisted_3 = ["onDblclick"]
const _hoisted_4 = { class: "name" }
const _hoisted_5 = { class: "size" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("i", { class: "far fa-times-circle" }, null, -1))
const _hoisted_8 = [
  _hoisted_7
]
const _hoisted_9 = ["accept", "multiple"]

import displayErrorMessage from "@/utils/ErrorHandling";
import { DxButton } from "devextreme-vue";
import { computed, ref, watch, PropType, onMounted } from "vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'file-upload',
  props: {
  text: {
    type: String,
    default: "Upload a File",
  },
  /**
   * Determines whether to allow to load multiple files.
   * @type {boolean}
   * @default true
   */
  multiple: {
    type: Boolean,
    default: true,
  },
  /**
   * Determines whether the control is read only.
   * @type {boolean}
   * @default false
   */
  readOnly: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether to show list of files.
   * @type {boolean}
   * @default true
   */
  showFileList: {
    type: Boolean,
    default: true,
  },
  buttonBackground: {
    type: String,
    default: "white",
  },
  buttonTextColor: {
    type: String,
    default: "black",
  },
  /**
   * Determines whether to trigger file uploading.
   * @type {boolean}
   * @default false
   */
  triggerFileUploading: {
    type: Boolean,
    default: false,
  },
  /**
   * Determines whether to allow to reset control.
   * @type {boolean}
   * @default false
   */
  reset: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: "100%",
  },
  fileTypes: {
    type: String,
    default: "*/*",
  },
  defaultFiles: {
    type: Array as PropType<File[]>,
    default: () => [],
  },
  /**
   * Determines whether to hide buttons.
   * @type {boolean}
   * @default false
   */
  hideButtons: {
    type: Boolean,
    default: false,
  },
  /** Indicates the maximum size of a file in bytes that can be uploaded.
   * When set to 0, there is no max size
   * @type {Number}
   * @default 0
   */
  maxFileSize: {
    type: Number,
    default: 0,
  },
},
  emits: [
  "filesChanged",
  "uploadCancelled",
  "fileDoubleClicked",
],
  setup(__props, { emit }) {

const props = __props;

_useCssVars(_ctx => ({
  "a7a2cf4a": (props.width),
  "27bdcf6a": (props.buttonBackground),
  "38e1d90e": (props.buttonTextColor),
  "01c6e6fd": (fileWidth.value),
  "5858ec45": (removeFileHoverCursor.value),
  "c08406d8": (removeFileHoverColor.value)
}))



onMounted(() => {
  files.value = [...props.defaultFiles];
});

watch(
  () => props.defaultFiles,
  () => {
    files.value = [...props.defaultFiles];
  },
  { deep: true },
);

const fileUploadControl = ref<HTMLInputElement>();
const files = ref<File[]>();

watch(
  () => props.triggerFileUploading,
  () => {
    if (props.triggerFileUploading) {
      onClick();
    }
  },
);

/** Handles the Change event of the File Input */
const onFileChange = () => {
  if (!fileUploadControl.value?.files) {
    files.value = undefined;
    emit("filesChanged", undefined);
    return;
  }

  if (!validateFileSize()) {
    const msg = props.multiple
      ? `One or more selected files exceed the maximum file size of ${formatFileSize(
          props.maxFileSize,
        )}`
      : `The selected file exceeds the maximum file size of ${formatFileSize(
          props.maxFileSize,
        )}`;

    // Reset file list to avoid selected files lingering
    fileUploadControl.value.files = new DataTransfer().files;
    displayErrorMessage(msg);
    return;
  }

  if (props.multiple && files.value) {
    // So users can add files from different folders, update existing file array
    files.value.push(...fileUploadControl.value.files);
  } else {
    files.value = Array.from(fileUploadControl.value.files);
  }

  emit("filesChanged", files.value);
};

const validateFileSize = () => {
  if (props.maxFileSize === 0) {
    return true;
  }

  if (!fileUploadControl.value?.files) {
    return true;
  }

  for (let i = fileUploadControl.value.files.length - 1; i == 0; i--) {
    const file = fileUploadControl.value.files[i];

    if (file.size > props.maxFileSize) {
      return false;
    }
  }

  return true;
};



/** Handles the Click event of the DxButton */
const onClick = () => {
  if (!fileUploadControl.value) {
    return;
  }

  fileUploadControl.value.click();
};

/** Formats the passed file size into a user friendly file size */
const formatFileSize = (fileSize: number) => {
  // Mb
  if (fileSize >= 1000000) {
    const mbFileSize = fileSize / 1000000;
    /* For mb, rounding to the nearest mb is a considerable difference.
     * For better accuracy, round to the nearest 0.1 mb */
    return mbFileSize % 1 == 0
      ? `${mbFileSize.toFixed(0)}MB`
      : `${mbFileSize.toFixed(1)}MB`;
  }

  // Kb
  if (fileSize >= 1000) {
    return (fileSize / 1000).toFixed(0) + "KB";
  }

  // bytes
  return fileSize + "B";
};

/** Handles the Remove icon click event */
const onRemoveClick = (index: number) => {
  if (!fileUploadControl.value?.files || props.readOnly) {
    return;
  }

  /* As the FileList in FileUploadControl.Files is read only,
   * we need to create a new FileList and assign it to the control
   * To do this we'll use the DataTransfer interface that the FileList implements */
  const dataTransfer = new DataTransfer();

  if (!files.value || files.value.length == 0) {
    emit("filesChanged", files.value);
    return;
  }

  for (let i = 0; i < files.value.length; i++) {
    if (i != index) {
      dataTransfer.items.add(files.value[i]);
    }
  }

  fileUploadControl.value.files = dataTransfer.files;

  // So files are removed from the screen, we need to sync the files array with the new FileList
  files.value = Array.from(fileUploadControl.value.files);
  emit("filesChanged", files.value);
};

const removeFileHoverCursor = computed(() =>
  !props.readOnly ? "pointer" : "",
);
const removeFileHoverColor = computed(() =>
  !props.readOnly ? "#4A4A4A" : "#A1A1A1",
);

watch(
  () => props.reset,
  () => {
    if (props.reset) {
      if (!fileUploadControl.value?.files) {
        return;
      }

      /* As the FileList in FileUploadControl.Files is read only,
       * we need to create a new FileList and assign it to the control
       * To do this we'll use the DataTransfer interface that the FileList implements */
      const dataTransfer = new DataTransfer();

      fileUploadControl.value.files = dataTransfer.files;

      // So files are removed from the screen, we need to sync the files array with the new FileList
      files.value = Array.from(fileUploadControl.value.files);
      emit("filesChanged", files.value);
    }
  },
);

/** Handles the user cancelling the file upload dialog */
const onCancel = () => emit("uploadCancelled");

const fileWidth = computed(() => (!props.multiple ? "100%" : "45%"));

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_unref(DxButton), {
        text: __props.text,
        onClick: onClick,
        width: "130px",
        disabled: __props.readOnly,
        visible: !props.hideButtons
      }, null, 8, ["text", "disabled", "visible"]),
      (files.value && __props.showFileList)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(files.value, (file, index) => {
              return (_openBlock(), _createElementBlock("div", {
                class: "file",
                key: index,
                onDblclick: ($event: any) => (emit('fileDoubleClicked', file))
              }, [
                _createElementVNode("span", _hoisted_4, _toDisplayString(file.name), 1),
                _createElementVNode("span", _hoisted_5, _toDisplayString(formatFileSize(file.size)), 1),
                (!props.hideButtons)
                  ? (_openBlock(), _createElementBlock("span", {
                      key: 0,
                      class: "remove",
                      onClick: ($event: any) => (onRemoveClick(index))
                    }, _hoisted_8, 8, _hoisted_6))
                  : _createCommentVNode("", true)
              ], 40, _hoisted_3))
            }), 128))
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("input", {
      accept: __props.fileTypes,
      class: "hidden",
      multiple: __props.multiple,
      ref_key: "fileUploadControl",
      ref: fileUploadControl,
      type: "file",
      onChange: onFileChange,
      onCancel: onCancel
    }, null, 40, _hoisted_9)
  ], 64))
}
}

})